import { useCallback, useRef, useState } from "react";
import * as http from "../../../../utils/http";
import { SortByType } from "../../types";
import { serializeFiltersForServer } from "../../helpersV2";
import { getFavoriteProductCatalogIds } from "../../../../utils/favoritesStorage";

const PAGE_SIZE = 50;
const DEBOUNCE_MS = 300;

type FilterDataItem = { value: string; isSelected: boolean; name: string; keyWordTitle: string; filterFn: (product: any) => boolean };
type FilterData = Record<string, FilterDataItem[]>;

interface UseCTProductSearchParams {
  categoryCatalogId: number;
  countryId: string;
  languageId: string;
  manufacturerId?: number | null;
  parseProducts: (products: any[], isExpressFlow: boolean, stockCodesInRange: string[]) => any[];
  isExpressFlow: boolean;
  stockCodesInRange: string[];
  isSwiss: boolean;
  isCannabis: boolean;
  shippingMethod: string;
  purchaseTier: string;
  selectedStockCode: string | null;
  externalPharmacyIds: string[];
  availablePharmacyStockCodes: string[];
}

export const useCTProductSearch = (params: UseCTProductSearchParams) => {
  const {
    categoryCatalogId,
    countryId,
    languageId,
    manufacturerId,
    parseProducts,
    isExpressFlow,
    stockCodesInRange,
    isSwiss,
    isCannabis,
    shippingMethod,
    purchaseTier,
    selectedStockCode,
    externalPharmacyIds,
    availablePharmacyStockCodes,
  } = params;

  const [serverProducts, setServerProducts] = useState<any[]>([]);
  const [totalProducts, setTotalProducts] = useState(0);
  const [currentFrom, setCurrentFrom] = useState(PAGE_SIZE);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [serverInitialized, setServerInitialized] = useState(false);

  // Search and pagination get separate abort controllers: a new search should cancel an
  // in-flight search (and any pagination it supersedes), but one loadMore must never cancel
  // another. Sharing a single controller made every infinite-scroll request abort its
  // predecessor, so no page ever completed.
  const abortControllerRef = useRef<AbortController | null>(null);
  const loadMoreAbortRef = useRef<AbortController | null>(null);
  // Synchronous mirror of isLoadingMore. Scroll fires many times per frame and setState is
  // async, so the state flag alone lets several loadMore calls through before it flips.
  const isLoadingMoreRef = useRef(false);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastFilterParamsRef = useRef<Record<string, any>>({});
  // Raw server products the initial load marked preSelected (deep-linked p1/q1, with the
  // Flowzz Premium price override applied). The DB-direct page kept one object per product for
  // the whole session, so that flag and those prices survived every filter change;
  // ct-products-search knows nothing about them, so its copy of such a product is replaced by
  // the initial one before parsing.
  const pinnedProductsRef = useRef<Map<number, any>>(new Map());

  const fetchProducts = useCallback(
    (filterParams: Record<string, any>, from: number, append: boolean) => {
      let controller: AbortController;
      if (append) {
        loadMoreAbortRef.current?.abort();
        controller = new AbortController();
        loadMoreAbortRef.current = controller;
      } else {
        // A fresh search invalidates both the previous search and any pending page.
        abortControllerRef.current?.abort();
        loadMoreAbortRef.current?.abort();
        controller = new AbortController();
        abortControllerRef.current = controller;
      }

      const body = {
        category_catalog_id: categoryCatalogId,
        country_id: countryId,
        language_id: languageId,
        manufacturer_id: manufacturerId || undefined,
        from,
        size: PAGE_SIZE,
        in_stock_only: isSwiss && isCannabis ? true : undefined,
        ...filterParams,
      };

      return http
        .postToServer("ct-products-search", body, [], { signal: controller.signal, skipDedup: true })
        .then((response: any) => {
          if (controller.signal.aborted) return;
          const { products: rawProducts, total } = response.data;
          const pinned = pinnedProductsRef.current;
          const parsed = parseProducts(
            rawProducts.map((product: any) => pinned.get(product.product_id) ?? product),
            isExpressFlow,
            stockCodesInRange,
          );

          if (append) {
            // A product promoted to the head of the list before its own page loads must not show twice.
            setServerProducts((prev: any[]) => {
              const loaded = new Set(prev.map((product) => product.product_id));
              return [...prev, ...parsed.filter((product) => !loaded.has(product.product_id))];
            });
          } else {
            setServerProducts(parsed);
          }
          setTotalProducts(total);
          setCurrentFrom(from + PAGE_SIZE);
        })
        .catch((err: any) => {
          if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
          window.clientLogger.error("CT product search failed", err);
        });
    },
    [categoryCatalogId, countryId, languageId, manufacturerId, parseProducts, isExpressFlow, stockCodesInRange, isSwiss, isCannabis]
  );

  const searchProducts = useCallback(
    (
      filterData: FilterData | undefined,
      searchTerm: string,
      sortBy: SortByType,
      excludeProductIds?: number[],
    ) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        const favoriteCatalogIds = getFavoriteProductCatalogIds();
        const expressStockCodes = isExpressFlow ? stockCodesInRange : undefined;
        const filterParams = serializeFiltersForServer(
          filterData,
          searchTerm,
          sortBy,
          excludeProductIds,
          favoriteCatalogIds,
          shippingMethod,
          purchaseTier,
          expressStockCodes,
          selectedStockCode,
          externalPharmacyIds,
          availablePharmacyStockCodes,
        );

        lastFilterParamsRef.current = filterParams;
        setIsSearching(true);

        fetchProducts(filterParams, 0, false).finally(() => {
          setIsSearching(false);
        });
      }, DEBOUNCE_MS);
    },
    [fetchProducts, isExpressFlow, stockCodesInRange, shippingMethod, purchaseTier, selectedStockCode, externalPharmacyIds, availablePharmacyStockCodes]
  );

  const loadMore = useCallback(() => {
    if (isLoadingMoreRef.current || currentFrom >= totalProducts) return;

    isLoadingMoreRef.current = true;
    setIsLoadingMore(true);
    fetchProducts(lastFilterParamsRef.current, currentFrom, true).finally(() => {
      isLoadingMoreRef.current = false;
      setIsLoadingMore(false);
    });
  }, [currentFrom, totalProducts, fetchProducts]);

  const hasMore = currentFrom < totalProducts;

  // `initialFilterParams` is the scope the server used for the first page; loadMore keeps paging
  // inside it until the first user-driven search replaces it. `pinnedRawProducts` are the
  // unparsed server products to keep over later search results (see pinnedProductsRef).
  const resetServerProducts = useCallback((products: any[], total: number, initialFilterParams: Record<string, any> = {}, pinnedRawProducts: any[] = []) => {
    pinnedProductsRef.current = new Map(pinnedRawProducts.map((product) => [product.product_id, product]));
    setServerProducts(products);
    setTotalProducts(total);
    setCurrentFrom(PAGE_SIZE);
    lastFilterParamsRef.current = initialFilterParams;
    setServerInitialized(true);
  }, []);

  // Moves these products to the head of the list, adding any that are not loaded yet. The
  // DB-direct page sorted its full list this way when the deep-linked quantity exceeds the
  // cannabis quota, so the customer sees what to reduce.
  const promoteToTop = useCallback((productsToPromote: any[]) => {
    const promotedIds = new Set(productsToPromote.map((product) => product.product_id));
    setServerProducts((prev: any[]) => [...productsToPromote, ...prev.filter((product) => !promotedIds.has(product.product_id))]);
  }, []);

  return {
    searchProducts,
    loadMore,
    serverProducts,
    totalProducts,
    hasMore,
    isSearching,
    isLoadingMore,
    resetServerProducts,
    promoteToTop,
    serverInitialized,
  };
};
